import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollContainer, getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);

    // FLOLAE_SCROLL_RESTORE (2026-10-09): 상품 상세에서 뒤로 왔을 때 보던 카드 위치로 복원
    this.addEventListener('click', this.#floRemember, true);
    window.addEventListener('pagehide', this.#floPageHide);
    this.#floRestore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.removeEventListener('click', this.#floRemember, true);
    window.removeEventListener('pagehide', this.#floPageHide);
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // FLOLAE_PREFETCH_MARGIN (2026-10-06): append the (already prefetched) next page ~1.5 screens early so its
          // images start downloading before the shopper reaches it (image requests take seconds on slow networks)
          rootMargin: '1500px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  // ── FLOLAE_SCROLL_RESTORE (2026-10-09) ──────────────────────────────────────────────────────────
  // Horizon 은 pagehide 때 scrollTop 숫자만 저장하는데, 뒤로 왔을 때 문서는 `?page=N` 한 페이지만 렌더되므로
  // 그 값은 맞지 않고(0 또는 끝으로 잘림), 데스크톱은 `.page-wrapper` 가 스크롤 컨테이너라 브라우저 기본 복원도 없다.
  // 여기서는 "카드(li) + 뷰포트 기준 top" 을 기준점으로 저장하고, 같은 주소(`?page=P`)로 돌아오면 그 카드를 같은 자리에 놓는다.

  /** @type {boolean} 이 문서에서 카드 클릭으로 이미 저장했는지 */
  #floSaved = false;

  /**
   * 카드 안 클릭(capture 단계 — product-card 의 내비게이션보다 먼저, 동기적으로) 시 기준점 저장.
   * @param {Event} event
   */
  #floRemember = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event instanceof MouseEvent && event.button !== 0) return;
    if (event.target.closest('button, input, label, select, [tabindex="1"]')) return;
    const li = event.target.closest('li[ref="cards[]"]');
    if (!li || !this.contains(li)) return;
    const anchor = event.target.closest('a');
    if (anchor && !anchor.getAttribute('href')) return;
    this.#floSave(li);
    this.#floSaved = true;
  };

  /** 카드 클릭이 아닌 이탈(메뉴·검색 등) — 첫 번째로 보이는 카드를 기준점으로 저장 */
  #floPageHide = () => {
    if (this.#floSaved) return;
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    const li = cards.find((card) => card.getBoundingClientRect().bottom > headerHeight);
    if (li instanceof HTMLElement) this.#floSave(li);
  };

  /**
   * @param {HTMLElement} li
   */
  #floSave(li) {
    if (!li.id || window.Shopify?.designMode) return;
    try {
      const url = new URL(window.location.href);
      if (li.dataset.page) url.searchParams.set('page', li.dataset.page);
      url.hash = `#${li.id}`;
      const record = {
        href: url.pathname + url.search,
        id: li.id,
        top: li.getBoundingClientRect().top,
        ts: Date.now(),
      };
      sessionStorage.setItem(PaginatedList.FLO_RESTORE_KEY, JSON.stringify(record));
      // 돌아올 주소가 기준 카드의 페이지를 렌더하도록 지금(내비게이션 시작 전) 동기적으로 맞춘다
      history.replaceState(history.state, '', url.toString());
    } catch (_) {
      // sessionStorage 불가(프라이빗 모드 등)·replaceState 거부 시 조용히 포기 — Horizon 기본 동작으로
    }
  }

  /** 뒤로/앞으로 와서 같은 주소(`?page=P`)면 기준 카드를 저장된 자리에 놓는다 */
  #floRestore() {
    let record;
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (!nav || /** @type {PerformanceNavigationTiming} */ (nav).type !== 'back_forward') return;
      record = JSON.parse(sessionStorage.getItem(PaginatedList.FLO_RESTORE_KEY) || 'null');
    } catch (_) {
      return;
    }
    if (!record || record.href !== window.location.pathname + window.location.search) return;
    if (!document.getElementById(record.id)) return;
    // 스크립트가 늦게 도착해(느린 회선) 사용자가 이미 보고 있을 수 있으면 건너뜀 — 주소 해시(#li)로 카드는 이미 화면 위에 있다
    if (performance.now() > 3000) return;

    // 이 페이지는 우리가 복원한다(모바일 기본 복원이 옛 scrollTop 으로 되돌리는 것 방지)
    history.scrollRestoration = 'manual';

    // 복원 동안은 즉시 스크롤 — `.page-wrapper` 의 scroll-behavior:smooth 와 해시(#li) 스크롤 애니메이션이
    // 우리 위치를 덮어쓰지 않게 하고, 사용자가 스크롤을 시작하면 더 건드리지 않는다
    const container = getScrollContainer();
    const previousBehavior = container instanceof HTMLElement ? container.style.scrollBehavior : '';
    if (container instanceof HTMLElement) container.style.scrollBehavior = 'auto';
    let done = false;
    const stop = () => {
      if (done) return;
      done = true;
      if (container instanceof HTMLElement) container.style.scrollBehavior = previousBehavior;
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((name) => window.removeEventListener(name, stop, true));
    };
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((name) =>
      window.addEventListener(name, stop, { capture: true, passive: true })
    );
    const apply = () => {
      if (done) return;
      const el = document.getElementById(record.id);
      if (!el) return;
      const diff = el.getBoundingClientRect().top - record.top;
      if (Math.abs(diff) < 1) return;
      container.scrollBy({ top: diff, behavior: 'instant' });
    };
    // load 뒤 몇 번 더 맞추고 끝낸다(Horizon scroll-container.js 의 pageshow 복원과 해시 스크롤이 그 사이에 끼어든다)
    const finish = () => {
      apply();
      setTimeout(apply, 0);
      setTimeout(apply, 150);
      setTimeout(() => {
        apply();
        stop();
      }, 400);
    };

    apply();
    setTimeout(apply, 0);
    window.addEventListener(
      'pageshow',
      (event) => {
        if (/** @type {PageTransitionEvent} */ (event).persisted) return stop();
        apply();
        setTimeout(apply, 0);
      },
      { once: true }
    );
    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', finish, { once: true });
  }

  static FLO_RESTORE_KEY = 'flo:list-restore';
  // ── /FLOLAE_SCROLL_RESTORE ───────────────────────────────────────────────────────────────────────

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
